import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { HelpService, HelpArticle, GuidedTour } from './help.service';
import { LoginService } from '../../login/login.service';
import { Permissions } from '../../auth-guard.guard';

@Component({
  selector: 'app-help-drawer',
  templateUrl: './help-drawer.component.html',
  styleUrls: ['./help-drawer.component.css']
})
export class HelpDrawerComponent implements OnInit, OnDestroy {
  private subscription!: Subscription;
  private currentUser: any;
  private readonly categoryRoutes: Record<string, string[]> = {
    'getting-started': [],
    masters: ['/product', '/doctor'],
    'job-entry': ['/joborder'],
    production: ['/jobschedule', '/production-transfer'],
    'challan-invoice': ['/challan', '/invoice'],
    accounting: ['/accounting'],
    reports: ['/reports'],
    admin: ['/access-control', '/company'],
  };

  searchQuery = '';
  searchResults: HelpArticle[] = [];
  currentArticle: HelpArticle | null = null;
  isOpen = false;

  constructor(
    public helpService: HelpService,
    private auth: LoginService,
    private permissions: Permissions,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.currentUserValue;
    this.subscription = this.helpService.drawerOpen$.subscribe(open => {
      this.isOpen = open;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  close(): void {
    this.helpService.closeDrawer();
    this.currentArticle = null;
    this.clearSearch();
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    if (query.length >= 2) {
      this.searchResults = this.helpService
        .searchArticles(query)
        .filter((article) => this.canViewArticle(article));
    } else {
      this.searchResults = [];
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
  }

  openArticle(article: HelpArticle): void {
    if (!this.canViewArticle(article)) {
      return;
    }
    this.currentArticle = article;
  }

  goBack(): void {
    this.currentArticle = null;
  }

  getArticles(categoryId: string): HelpArticle[] {
    if (!this.canViewCategory(categoryId)) {
      return [];
    }
    return this.helpService
      .getArticlesByCategory(categoryId)
      .filter((article) => this.canViewArticle(article));
  }

  getArticleCount(categoryId: string): number {
    return this.getArticles(categoryId).length;
  }

  get visibleCategories(): { id: string; name: string; icon: string }[] {
    return this.helpService.categories.filter(
      (category) => this.canViewCategory(category.id) && this.getArticleCount(category.id) > 0,
    );
  }

  get visibleTours(): GuidedTour[] {
    return this.helpService.tours
      .map((tour) => ({
        ...tour,
        steps: tour.steps.filter((step) => this.canAccessAnyRoute(step.routes)),
      }))
      .filter((tour) => tour.steps.length > 0);
  }

  getCategoryIcon(categoryId: string): string {
    return this.helpService.categories.find(c => c.id === categoryId)?.icon || 'folder';
  }

  getCategoryName(categoryId: string): string {
    return this.helpService.categories.find(c => c.id === categoryId)?.name || categoryId;
  }

  getArticleById(id: string): HelpArticle | undefined {
    const article = this.helpService.getArticleById(id);
    return article && this.canViewArticle(article) ? article : undefined;
  }

  startTour(tour: GuidedTour): void {
    this.helpService.startTour(tour.id, tour);
  }

  getRenderedArticleContent(article: HelpArticle): string {
    let content = article.content;

    if (article.id === 'system-overview') {
      content = content.replace('{{CORE_MODULES_SECTION}}', this.buildCoreModulesSection());
      content = content.replace('{{TYPICAL_WORKFLOW_ROWS}}', this.buildWorkflowRows());
    }

    if (article.id === 'navigation-guide') {
      content = content.replace('{{NAVIGATION_MENU_ROWS}}', this.buildNavigationRows());
    }

    return content;
  }

  nextStep(): void {
    this.helpService.nextTourStep();
  }

  prevStep(): void {
    this.helpService.prevTourStep();
  }

  endTour(): void {
    this.helpService.endTour();
  }

  private canViewArticle(article: HelpArticle): boolean {
    return this.canViewCategory(article.category);
  }

  private canViewCategory(categoryId: string): boolean {
    const routes = this.categoryRoutes[categoryId] || [];
    return this.canAccessAnyRoute(routes);
  }

  private canAccessAnyRoute(routes?: string[]): boolean {
    if (!routes || routes.length === 0) {
      return true;
    }
    return routes.some((route) => this.permissions.canActivate(this.currentUser?.user, route));
  }

  private buildCoreModulesSection(): string {
    const modules = [
      {
        title: 'Dashboard',
        routes: ['/dashboard'],
        content:
          'Your home page shows real-time lab performance:\n- **Job count cards** — Open, In Progress, Completed, and Delivered totals for the current period\n- **Late delivery window** — Review delayed jobs by the selected aging bucket\n- **Top doctors & top products** — Quickly identify major contributors and demand trends\n- **Financial year & date range filters** — Narrow the dashboard to any period',
      },
      {
        title: 'Masters',
        routes: ['/product', '/doctor'],
        content:
          'Configure foundational data including products, product categories, processes, doctors, areas, states, shades, shipment types, HSN/SAC codes, price lists, and job checklists.',
      },
      {
        title: 'Job Entry',
        routes: ['/joborder'],
        content:
          'Create and manage dental job orders with patient details, product selection, process routing, and document attachments.',
      },
      {
        title: 'Production',
        routes: ['/jobschedule', '/production-transfer'],
        content:
          'Track jobs through production stages using **Job In / Job Out**. Supports **barcode scanning**, rework tracking, and controlled employee-to-employee production transfer when a case needs reassignment.',
      },
      {
        title: 'Challan & Invoicing',
        routes: ['/challan', '/invoice'],
        content:
          'Generate delivery challans, proforma invoices, and final tax invoices. Link challans to invoices automatically.',
      },
      {
        title: 'Accounting',
        routes: ['/accounting'],
        content:
          'Manage accounts (cash, bank, and other types), receipts, credit/debit notes, journal vouchers, knock-offs, balances, and sales returns.',
      },
      {
        title: 'Reports',
        routes: ['/reports'],
        content:
          'Access analytical reports including doctor business summary, product usage summary, sales / communication person performance, pending jobs, aging, ledgers, and production detail exports.',
      },
      {
        title: 'Administration',
        routes: ['/access-control', '/company'],
        content:
          'Manage users, user roles, access control, employees (with PIN-based login), departments, company settings, and status-change remarks.',
      },
    ].filter((module) => this.canAccessAnyRoute(module.routes));

    if (modules.length === 0) {
      return 'Your role currently has limited module access. Contact your administrator if you need additional permissions.';
    }

    return modules
      .map((module, index) => `### ${index + 1}. ${module.title}\n${module.content}`)
      .join('\n\n');
  }

  private buildWorkflowRows(): string {
    const rows = [
      {
        step: 1,
        action: '**Setup Masters**',
        module: 'Products, processes, doctors, price lists',
        routes: ['/product', '/doctor'],
      },
      {
        step: 2,
        action: '**Create Job Order**',
        module: 'Patient info, products, process routing',
        routes: ['/joborder'],
      },
      {
        step: 3,
        action: '**Production**',
        module: 'Job In (receive) → Job Out (complete each process) → Transfer/Rework when needed',
        routes: ['/jobschedule', '/production-transfer'],
      },
      {
        step: 4,
        action: '**Delivery**',
        module: 'Create challan for completed jobs',
        routes: ['/challan'],
      },
      {
        step: 5,
        action: '**Invoice**',
        module: 'Generate proforma → Convert to final invoice',
        routes: ['/invoice'],
      },
      {
        step: 6,
        action: '**Payment**',
        module: 'Record account receipts, knock-offs',
        routes: ['/accounting'],
      },
    ].filter((row) => this.canAccessAnyRoute(row.routes));

    if (rows.length === 0) {
      return 'Your role can access only a limited part of the workflow.';
    }

    return [
      '| Step | Action | Module |',
      '|------|--------|--------|',
      ...rows.map((row) => `| ${row.step} | ${row.action} | ${row.module} |`),
    ].join('\n');
  }

  private buildNavigationRows(): string {
    const rows: string[] = [];

    if (this.canAccessAnyRoute(['/dashboard'])) {
      rows.push('| **Dashboard** | Overview with charts, late-delivery windows, top doctors, and top products |');
    }

    if (this.canAccessAnyRoute(['/product', '/doctor'])) {
      rows.push('| **Masters** | Products, Processes, Doctors, States, Areas, HSN/SAC, Shades, Shipments, Checklists, Price Lists |');
    }

    if (this.canAccessAnyRoute(['/joborder'])) {
      rows.push('| **Job Entry** | Job Orders, Remarks |');
    }

    const productionPages: string[] = [];
    if (this.canAccessAnyRoute(['/jobschedule'])) {
      productionPages.push('Job Schedule, Job In, Job Out, Employee Login');
    }
    if (this.canAccessAnyRoute(['/production-transfer'])) {
      productionPages.push('Production Transfer');
    }
    if (productionPages.length > 0) {
      rows.push(`| **Production** | ${productionPages.join(', ')} |`);
    }

    if (this.canAccessAnyRoute(['/challan'])) {
      rows.push('| **Challan** | Create/manage delivery challans |');
    }

    if (this.canAccessAnyRoute(['/invoice'])) {
      rows.push('| **Invoicing** | Proforma Invoices, Final Invoices |');
    }

    if (this.canAccessAnyRoute(['/accounting'])) {
      rows.push('| **Accounting** | Accounts, Receipts, Notes, Journal Vouchers, Knock-Off, Balance, Sales Return |');
    }

    if (this.canAccessAnyRoute(['/reports'])) {
      rows.push('| **Reports** | Business, operational, and export-ready analytical reports |');
    }

    if (this.canAccessAnyRoute(['/access-control', '/company'])) {
      rows.push('| **Admin** | Users, Roles, Access Control, Company, Employees, Departments |');
    }

    return rows.join('\n');
  }
}
